/* eslint import/no-unresolved: 0 */

import { useEffect, useState } from "react";
import { saveAffFromURL } from "utils/aff";

import Header from "components/Header";
import Footer from "components/Footer";
import { API_BASE } from "lib/http"; // 共通のAPI_BASEを使用

// 公開API（/api/public が正）
const API_PUBLIC = `${API_BASE}/api/public`;
// ログイン中ユーザー情報（/api/auth/me が正）
const API_ME = `${API_BASE}/api/auth/me`;

// ★ 実績は public じゃない環境があるので /api 側も候補に入れる
const API_APP = `${API_BASE}/api`;

type NoticeItem = {
  id: string;
  title: string;
  body?: string;
  startAt?: string;
  createdAt?: string;
  level?: "info" | "warn" | "alert";
  linkUrl?: string;
};

type Me = { id: string; name?: string; points: number };

/** ISO文字列 → 2月18日(水) 形式（年なし） */
const formatJPDateNoYear = (iso?: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const w = ["日", "月", "火", "水", "木", "金", "土"][d.getDay()];
  return `${m}月${day}日(${w})`;
};

/** ISO文字列 → 2025年12月6日(土) 形式（年あり：お知らせ等用） */
const formatJPDate = (iso?: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const w = ["日", "月", "火", "水", "木", "金", "土"][d.getDay()];
  return `${y}年${m}月${day}日(${w})`;
};

/** 金額表示（29,700 形式） */
const formatJPY = (n: any): string => {
  const num = Number(n);
  if (!isFinite(num)) return "";
  return num.toLocaleString("ja-JP");
};

type RecentHitItem = {
  // 1行目
  dateText: string;
  planText: string;

  // 2行目
  ticketText: string; // 例：3連単  1-4-2
  amountText: string; // 例：29,700 円 的中
};

export default function LP() {
  // LP流入時に aff コードを保存 ＋ ログイン中ユーザー取得
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    saveAffFromURL();

    (async () => {
      try {
        const r = await fetch(API_ME, { credentials: "include" });
        if (!r.ok) throw new Error(`ME ${r.status}`);
        const j = await r.json().catch(() => ({} as any));

        // レスポンス形の揺れ対応（{user:{...}} or そのまま {...}）
        const u: any = j?.user ?? j ?? {};

        // ID整形: 数字抽出 → 下6桁 → 先頭に 51 を付与
        const toDisplayId = (src: any): string | undefined => {
          const raw =
            src?.id ??
            src?.userId ??
            src?.uid ??
            src?.lineUserId ??
            src?.lineId ??
            "";
          const digits = String(raw).replace(/\D/g, "");
          if (!digits) return undefined;
          const last6 = digits.slice(-6).padStart(6, "0");
          return `51${last6}`;
        };

        const displayId = toDisplayId(u) ?? "";
        const displayName = (u?.lineName || u?.name || "").trim();
        const displayPoints = Number(
          typeof u?.points === "number"
            ? u.points
            : typeof u?.purchasePoint === "number"
            ? u.purchasePoint
            : 0
        );

        setMe({
          id: displayId, // Header 側でそのまま表示可能（数字文字列）
          name: displayName,
          points: displayPoints,
        });
      } catch {
        setMe(null);
      }
    })();
  }, []);

  // おすすめプランが1件以上あるかどうか（null = まだ判定前）
  const [hasRecommended, setHasRecommended] = useState<boolean | null>(null);

  // ★ おすすめプラン名（最大3件=3行まで）
  const [recommendedList, setRecommendedList] = useState<string[]>([]);

  // お知らせ（最新1件を使ってタイトル＋本文表示）
  const [news, setNews] = useState<NoticeItem[]>([]);

  // ★ 直近の的中実績（3件×2行＝計6行）
  const [recentHits, setRecentHits] = useState<RecentHitItem[]>([]);

  // ★ サイトの使い方：開閉
  const [howtoOpen, setHowtoOpen] = useState(false);

  // おすすめ判定 + お知らせ取得 + 直近実績取得
  useEffect(() => {
    // ▼ おすすめプラン判定
    (async () => {
      try {
        const r = await fetch(`${API_PUBLIC}/recommended?t=${Date.now()}`, {
          credentials: "include",
        });
        if (!r.ok) throw new Error(String(r.status));
        const j = await r.json();
        const rows = Array.isArray(j) ? j : [];

        setHasRecommended(rows.length > 0);

        // ★ 最大3件（3行まで）
        const names = rows
          .slice(0, 3)
          .map((x: any) => String(x?.name || "").trim())
          .filter(Boolean);

        setRecommendedList(names);
      } catch {
        // エラー時は「おすすめ無し扱い」
        setHasRecommended(false);
        setRecommendedList([]);
      }
    })();

    // ▼ お知らせ取得
    (async () => {
      try {
        const r = await fetch(`${API_PUBLIC}/notices?t=${Date.now()}`, {
          credentials: "include",
        });
        const j = await r.json();
        const rows: NoticeItem[] = (Array.isArray(j) ? j : [])
          .slice(0, 1) // ★ 最新1件だけ
          .map((n: any) => ({
            id: String(n.id),
            title: String(n.title || ""),
            body: typeof n.body === "string" ? n.body : "",
            startAt: n.startAt || "",
            createdAt: n.createdAt || n.date || "",
            level: n.level || "info",
            linkUrl: n.linkUrl || "",
          }));
        setNews(rows);
      } catch {
        setNews([]);
      }
    })();

    // ▼ 直近的中実績（3件）取得
    (async () => {
      const candidates = [
        // まず /api 側（ログイン前提）
        `${API_APP}/results/recent`,
        `${API_APP}/results/latest`,
        `${API_APP}/results`,
        `${API_APP}/result-history`,
        `${API_APP}/history/results`,
        // 次に /api/public 側
        `${API_PUBLIC}/results`,
        `${API_PUBLIC}/results/recent`,
        `${API_PUBLIC}/results/latest`,
        `${API_PUBLIC}/recent-results`,
        `${API_PUBLIC}/result-history`,
      ];

      const pickRows = (j: any): any[] => {
        if (Array.isArray(j)) return j;
        if (Array.isArray(j?.rows)) return j.rows;
        if (Array.isArray(j?.data)) return j.data;
        if (Array.isArray(j?.results)) return j.results;
        if (Array.isArray(j?.items)) return j.items;
        if (Array.isArray(j?.list)) return j.list;
        return [];
      };

      const fetchFirstArray = async (): Promise<any[]> => {
        for (const url of candidates) {
          try {
            const r = await fetch(`${url}?t=${Date.now()}`, {
              credentials: "include",
            });
            if (!r.ok) continue;
            const j = await r.json().catch(() => null);
            const rows = pickRows(j);
            if (rows.length > 0) return rows;
          } catch {
            // 次候補へ
          }
        }
        return [];
      };

      // ★ 文字列取得ヘルパ（ネストも含めて拾う）
      const pickStr = (x: any, getters: Array<(o: any) => any>): string => {
        for (const g of getters) {
          const v = g(x);
          if (typeof v === "string" && v.trim()) return v.trim();
          if (typeof v === "number" && isFinite(v)) return String(v);
        }
        return "";
      };

      try {
        const rows = await fetchFirstArray();

        const normalized: RecentHitItem[] = rows.slice(0, 3).map((x: any) => {
          const iso =
            x?.startAt ||
            x?.date ||
            x?.hitAt ||
            x?.createdAt ||
            x?.purchasedAt ||
            x?.resultAt ||
            x?.at ||
            "";
          // ★ 年なし
          const dateText = formatJPDateNoYear(iso) || "";

          const planText =
            pickStr(x, [
              (o) => o?.planName,
              (o) => o?.plan,
              (o) => o?.plan_title,
              (o) => o?.planTitle,
              (o) => o?.plan?.name,
              (o) => o?.plan?.title,
              (o) => o?.name,
              (o) => o?.title,
            ]) || "";

          // ★ /api/public/results は item に「3連単　1-4-2」が入る
          const itemText = pickStr(x, [
            (o) => o?.item,
            (o) => o?.buyItem,
            (o) => o?.betItem,
            (o) => o?.ticketItem,
            (o) => o?.wager,
            (o) => o?.summary,
            (o) => o?.detail,
          ]);

          // 旧互換（念のため残す）
          let ticket = pickStr(x, [
            (o) => o?.ticketType,
            (o) => o?.betType,
            (o) => o?.type,
            (o) => o?.method,
            (o) => o?.kind,
            (o) => o?.ticket,
            (o) => o?.kinds,
            (o) => o?.wagerType,
            (o) => o?.betKind,
            (o) => o?.ticketKind,
            (o) => o?.kindName,
            (o) => o?.ticket?.type,
            (o) => o?.ticket?.kind,
            (o) => o?.ticket?.name,
            (o) => o?.ticket?.label,
            (o) => o?.bet?.type,
            (o) => o?.bet?.kind,
          ]);

          const bet = pickStr(x, [
            (o) => o?.bet,
            (o) => o?.buy,
            (o) => o?.kaime,
            (o) => o?.combo,
            (o) => o?.result,
            (o) => o?.bets,
            (o) => o?.line,
            (o) => o?.selection,
            (o) => o?.buyme,
            (o) => o?.ticket?.bet,
            (o) => o?.ticket?.combo,
            (o) => o?.bet?.combo,
            (o) => o?.bet?.line,
          ]);

          if (!ticket && bet && /^\d\s*[-/]\s*\d\s*[-/]\s*\d/.test(bet)) {
            ticket = "3連単";
          }

          // ★ item があれば item（=券種+買い目）を最優先
          const ticketText = (
            itemText || `${ticket}${ticket && bet ? "  " : ""}${bet}`
          )
            .replace(/\s+/g, " ")
            .trim();

          const amtRaw =
            x?.amount ??
            x?.payout ??
            x?.money ??
            x?.hitAmount ??
            x?.pay ??
            x?.return ??
            x?.profit ??
            x?.hit ??
            0;

          const amt = formatJPY(amtRaw);
          const amountText = amt ? `${amt} 円 的中` : `的中`;

          return { dateText, planText, ticketText, amountText };
        });

        const cleaned = normalized.filter(
          (it) =>
            (it.dateText || it.planText || it.ticketText || it.amountText).trim() !== ""
        );

        setRecentHits(cleaned);
      } catch {
        setRecentHits([]);
      }
    })();
  }, []);

  const latest = news[0];
  const latestDate = formatJPDate(latest?.startAt || latest?.createdAt);
  const latestBodyLines: string[] =
    latest?.body
      ?.split(/\r?\n/)
      .map((s: string) => s.trim())
      .filter(Boolean) || [];

  // ====== 素材（public/assets/member/） ======
  const imgKoukai = `/assets/member/home1-koukai.webp`;
  const imgJisseki = `/assets/member/home2-jisseki.webp`;
  const imgNoticePlate = `/assets/member/home1-preat.webp`;

  const btnSenryaku = `/assets/member/senryaku-bt.webp`;
  const btnSyousai = `/assets/member/syousai-bt.webp`;
  const btnJissekiMore = `/assets/member/jisseki-bt.webp`;
  const btnOshirase = `/assets/member/oshirase-bt.webp`;

  const bannerTeleboat = `/assets/member/teleboat-bt.webp`;
  const bannerBoatrace = `/assets/member/boatrace-bt.webp`;

  return (
    <main className="lp" style={{ background: "#ffffff" }}>
      <Header userId={me?.id} userName={me?.name} points={me?.points} />

      {/* ✅ 追加：サイトの使い方（友だち追加50pt帯の“下”に置きたいので Header 直下へ） */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 10px" }}>
        <button
          type="button"
          className={`howtoToggle ${howtoOpen ? "isOpen" : ""}`}
          onClick={() => setHowtoOpen((v) => !v)}
          aria-expanded={howtoOpen}
          aria-controls="howtoPanel"
        >
          <span className="howtoToggle__left">
            <span className="howtoToggle__icon">📘</span>
            <span className="howtoToggle__label">サイトの使い方</span>
          </span>
          <span className="howtoToggle__chev">{howtoOpen ? "−" : "+"}</span>
        </button>

        <div
          id="howtoPanel"
          className={`howtoPanel ${howtoOpen ? "isOpen" : ""}`}
          aria-hidden={!howtoOpen}
        >
          <div className="howtoPanel__inner">
            <div className="howtoTitle">5ステップで完了</div>
            <ol className="howtoList">
              <li>
                参加したいプランに必要なだけのポイントを購入
              </li>
              <li>
                購入したポイントを使用してプランを購入
              </li>
              <li>
                <b>13時半</b>に買い目予想がマイページで公開されるので確認
              </li>
              <li>
                公開された買い目予想どおりに競艇のネット投票サイトで舟券を購入（テレボート、ボートレースなど）
              </li>
              <li>
                レース終了後、ネット投票サイトで的中結果を確認
              </li>
            </ol>
            <div className="howtoNote">
              ※ 迷ったら「販売プラン一覧 → 購入 → マイページ確認」の順でOKです。
            </div>
          </div>
        </div>
      </div>

      {/* LP.tsx 内だけで完結する最小CSS */}
      <style>{`
        .homeWrap { max-width: 720px; margin: 0 auto; padding: 10px; }
        .cardImg { position: relative; width: 100%; border-radius: 14px; overflow: hidden; }
        .cardImg img.bg { width: 100%; height: auto; display: block; }
        .mt10{ margin-top: 10px; }
        .mt12{ margin-top: 12px; }
        .mt16{ margin-top: 16px; }
        .mt20{ margin-top: 20px; }
        .center { text-align: center; }
        .banner { width: 92%; max-width: 520px; height: auto; display: block; margin: 0 auto; }

        /* ✅ 追加：サイトの使い方（帯＋開閉パネル） */
        .howtoToggle{
          width: 100%;
          margin: 10px auto 8px;
          padding: 12px 14px;
          border-radius: 999px;
          border: 1px solid rgba(0,0,0,.12);
          background: rgba(255,255,255,.78);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          cursor: pointer;
          text-align: left;
          color: #1b2a33;
          box-shadow: 0 6px 18px rgba(0,0,0,.06);
        }
        .howtoToggle:hover{ opacity: .98; }
        .howtoToggle__left{
          display:flex;
          align-items:center;
          gap:10px;
          min-width:0;
        }
        .howtoToggle__icon{ font-size: 16px; }
        .howtoToggle__label{
          font-weight: 900;
          letter-spacing: .02em;
          white-space: nowrap;
        }
        .howtoToggle__chev{
          font-weight: 900;
          font-size: 18px;
          line-height: 1;
          opacity: .85;
        }

        .howtoPanel{
          overflow: hidden;
          max-height: 0;
          opacity: 0;
          transform: translateY(-4px);
          transition: max-height .35s ease, opacity .25s ease, transform .25s ease;
        }
        .howtoPanel.isOpen{
          max-height: 420px;
          opacity: 1;
          transform: translateY(0);
        }
        .howtoPanel__inner{
          margin: 0 auto 6px;
          padding: 14px 14px 12px;
          border-radius: 14px;
          background: rgba(255,255,255,.88);
          border: 1px solid rgba(0,0,0,.10);
          box-shadow: 0 10px 28px rgba(0,0,0,.06);
          color: #1b2a33;
        }
        .howtoTitle{
          font-weight: 900;
          font-size: 14px;
          margin-bottom: 10px;
          letter-spacing: .02em;
        }
        .howtoList{
          margin: 0;
          padding-left: 18px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          font-size: 13px;
          line-height: 1.55;
        }
        .howtoList b{ font-weight: 900; }
        .howtoNote{
          margin-top: 10px;
          font-size: 12px;
          opacity: .86;
          line-height: 1.5;
        }

        /* カード下の大ボタン（センター） */
        .bigBtn {
          width: 72%;
          max-width: 360px;
          height: auto;
          display: inline-block;
        }

        /* ★ 公開情報カード内：白帯に「詳細を見る」を入れる（少し上へ） */
        .koukaiDetailInWhite {
          position: absolute;
          right: 22px;
          bottom: 36px;

          width: 150px;
          max-width: 44%;
          height: auto;

          display: block;
          z-index: 3;
        }
        .koukaiDetailInWhite:hover { opacity: .98; }

        /* =========================
           ★ おすすめ（1行固定 + 下線見える）
           - スマホは px 基準のまま
           - デスクトップは % 基準に切替（画像比率に追従） ← 今回の修正点
           ========================= */
        .koukaiRecoInImage {
          position: absolute;
          left: 26px;
          right: 26px;
          top: 74px;
          z-index: 4;

          display: flex;
          flex-direction: column;
          gap: 10px;
          box-sizing: border-box;

          max-height: 120px;
          overflow: hidden;

          pointer-events: none;
        }

        @media (min-width: 768px) {
          .koukaiRecoInImage{
            left: 5%;
            right: 5%;
            top: 24%;
            max-height: 34%;
            gap: 12px;
          }
        }
        @media (min-width: 1100px) {
          .koukaiRecoInImage{
            top: 26%;
          }
        }

        @keyframes recoBreath {
          0%   { color: #1b2a33; }
          50%  { color: #b79b4a; }
          100% { color: #1b2a33; }
        }

        @keyframes recoUnderlineFlow {
          0%   { background-position: 0% 100%; }
          100% { background-position: 200% 100%; }
        }

        .koukaiRecoText {
          position: relative;
          font-weight: 900;
          font-size: clamp(13px, 4.2vw, 16px);
          line-height: 1.22;
          letter-spacing: .01em;

          white-space: nowrap;
          overflow: hidden;
          text-overflow: clip;

          padding-bottom: 8px;

          text-shadow:
            0 1px 2px rgba(255,255,255,.75),
            0 2px 10px rgba(0,0,0,.12);

          animation: recoBreath 2.8s ease-in-out infinite;
        }

        .koukaiRecoText::after {
          content: "";
          position: absolute;
          left: 0;
          width: min(92%, 520px);
          bottom: 2px;
          height: 3px;
          border-radius: 999px;

          background-image: linear-gradient(
            90deg,
            rgba(183,155,74,0) 0%,
            rgba(183,155,74,.55) 18%,
            rgba(183,155,74,1) 50%,
            rgba(183,155,74,.55) 82%,
            rgba(183,155,74,0) 100%
          );
          background-size: 200% 100%;
          background-position: 0% 100%;
          animation: recoUnderlineFlow 2.7s linear infinite;

          opacity: .98;
          filter:
            drop-shadow(0 1px 2px rgba(255,255,255,.65))
            drop-shadow(0 2px 8px rgba(0,0,0,.12));
        }

        .koukaiRecoText.d1 { animation-delay: 0s; }
        .koukaiRecoText.d2 { animation-delay: .2s; }
        .koukaiRecoText.d3 { animation-delay: .4s; }

        .koukaiRecoText.d1::after { animation-delay: 0s; }
        .koukaiRecoText.d2::after { animation-delay: .18s; }
        .koukaiRecoText.d3::after { animation-delay: .36s; }

        /* =========================
           ★ 直近的中実績（枠と金額：点滅ゴールド）
           ========================= */
        .hitsInCard {
          position: absolute;
          left: 5%;
          right: 5%;
          top: 22%;

          z-index: 4;
          box-sizing: border-box;

          display: flex;
          flex-direction: column;
          gap: clamp(10px, 1.6vw, 14px);

          pointer-events: none;
        }

        .hitBlock {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 6px;

          padding: 8px 10px;
          border-radius: 10px;
        }

        /* ★ 点滅：速すぎ改善（2.4s） */
        @keyframes goldBlink {
          0% {
            opacity: .50;
            filter: drop-shadow(0 1px 2px rgba(255,255,255,.40)) drop-shadow(0 2px 10px rgba(0,0,0,.10));
          }
          50% {
            opacity: 1;
            filter: drop-shadow(0 1px 2px rgba(255,255,255,.75)) drop-shadow(0 3px 18px rgba(183,155,74,.38));
          }
          100% {
            opacity: .50;
            filter: drop-shadow(0 1px 2px rgba(255,255,255,.40)) drop-shadow(0 2px 10px rgba(0,0,0,.10));
          }
        }

        /* ★ 枠（太く：3px）＋点滅ゴールド */
        .hitBlock::after {
          content: "";
          position: absolute;
          inset: -2px;
          border-radius: 12px;

          border: 3px solid rgba(183,155,74,.98);
          box-shadow:
            0 0 0 1px rgba(255,255,255,.55) inset,
            0 2px 10px rgba(0,0,0,.10);

          animation: goldBlink 2.4s ease-in-out infinite;
          pointer-events: none;
          z-index: 0;
        }

        .hitLine1, .hitLine2 {
          position: relative;
          z-index: 1;

          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;

          font-weight: 900;
          font-size: clamp(14px, 4.6vw, 18px);
          line-height: 1.12;
          letter-spacing: .01em;
          color: #1b2a33;

          text-shadow:
            0 1px 2px rgba(255,255,255,.85),
            0 2px 12px rgba(0,0,0,.10);

          white-space: nowrap;
        }

        .hitBlock.d1::after { animation-delay: 0s; }
        .hitBlock.d2::after { animation-delay: .18s; }
        .hitBlock.d3::after { animation-delay: .36s; }

        .hitLeft {
          flex: 1 1 auto;
          min-width: 0;
          overflow: hidden;
          text-overflow: clip;
        }
        .hitRight {
          flex: 0 0 auto;
          text-align: right;
        }

        .hitTicketLeft { opacity: .98; }

        .hitAmount {
          font-weight: 900;
          font-size: clamp(16px, 5.2vw, 22px);
          color: rgba(183,155,74,1);

          animation: goldBlink 2.4s ease-in-out infinite;
          will-change: opacity, filter;
        }
        .hitAmount.d1 { animation-delay: 0s; }
        .hitAmount.d2 { animation-delay: .18s; }
        .hitAmount.d3 { animation-delay: .36s; }

        .hitEmpty {
          font-weight: 900;
          font-size: clamp(14px, 4.6vw, 18px);
          color: rgba(27,42,51,.88);
          text-shadow: 0 1px 2px rgba(255,255,255,.85);
        }

        /* ===== ふわふわ（ボタンだけ） ===== */
        @keyframes floaty {
          0%   { transform: translate3d(0, 0, 0); }
          50%  { transform: translate3d(0, -6px, 0); }
          100% { transform: translate3d(0, 0, 0); }
        }
        .floaty {
          animation: floaty 3.2s ease-in-out infinite;
          will-change: transform;
        }
        .btnSyousaiFloat { animation-delay: 0s; }
        .btnSenryakuFloat { animation-delay: .15s; }
        .btnJissekiFloat { animation-delay: .25s; }
        .btnOshiraseFloat { animation-delay: .35s; }
      `}</style>

      <div className="homeWrap">
        {/* =========================
            ① home1-koukai.webp
           ========================= */}
        <section aria-label="本日の公開情報">
          <div className="cardImg">
            <img className="bg" src={imgKoukai} alt="本日の公開情報" />

            {recommendedList.length > 0 && (
              <div className="koukaiRecoInImage" aria-label="おすすめプラン">
                {recommendedList.map((name, idx) => {
                  const cls = idx === 0 ? "d1" : idx === 1 ? "d2" : "d3";
                  return (
                    <div
                      key={`${name}-${idx}`}
                      className={`koukaiRecoText ${cls}`}
                      title={name}
                    >
                      {name}
                    </div>
                  );
                })}
              </div>
            )}

            <a href="/plans" aria-label="詳細を見る">
              <img
                className={`koukaiDetailInWhite floaty btnSyousaiFloat`}
                src={btnSyousai}
                alt="詳細を見る"
              />
            </a>

            <div style={{ display: "none" }}>
              {hasRecommended === null ? "loading" : hasRecommended ? "yes" : "no"}
            </div>
          </div>
        </section>

        {/* =========================
            ② senryaku-bt.webp（公開情報の下）
           ========================= */}
        <div className="center mt12">
          <a href="/plans" aria-label="戦略にアクセス">
            <img
              className={`bigBtn floaty btnSenryakuFloat`}
              src={btnSenryaku}
              alt="戦略にアクセス"
            />
          </a>
        </div>

        {/* =========================
            ③ home2-jisseki.webp（直近的中実績）
           ========================= */}
        <section aria-label="直近的中実績" className="mt16">
          <a
            href="/results"
            aria-label="直近的中実績を見る"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <div className="cardImg">
              <img className="bg" src={imgJisseki} alt="直近的中実績" />

              <div className="hitsInCard" aria-label="直近の実績（3件）">
                {recentHits.length === 0 ? (
                  <div className="hitEmpty">続きはまだありません。</div>
                ) : (
                  recentHits.slice(0, 3).map((it, idx) => {
                    const cls = idx === 0 ? "d1" : idx === 1 ? "d2" : "d3";
                    return (
                      <div
                        className={`hitBlock ${cls}`}
                        key={`${it.dateText}-${it.planText}-${idx}`}
                      >
                        <div className="hitLine1">
                          <div className="hitLeft">{it.dateText}</div>
                          <div className="hitRight">{it.planText}</div>
                        </div>

                        <div className="hitLine2">
                          <div className="hitLeft hitTicketLeft">{it.ticketText}</div>
                          <div className={`hitRight hitAmount ${cls}`}>{it.amountText}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </a>
        </section>

        {/* =========================
            ④ jisseki-bt.webp
           ========================= */}
        <div className="center mt12">
          <a href="/results" aria-label="実績をもっと見る">
            <img
              className={`bigBtn floaty btnJissekiFloat`}
              src={btnJissekiMore}
              alt="実績をもっと見る"
            />
          </a>
        </div>

        {/* =========================
            ⑤ 新パネルおしらせ（home1-preat.webp）
           ========================= */}
        <section className="mt20" aria-label="おしらせ">
          <a
            href="/notices"
            aria-label="お知らせ一覧へ"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                backgroundImage: `url(${imgNoticePlate})`,
                backgroundSize: "100% 100%",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                minHeight: 170,
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: 22,
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: "#1b2a33",
                    fontWeight: 900,
                    fontSize: 16,
                    marginBottom: 8,
                    textShadow: "0 1px 2px rgba(255,255,255,.6)",
                  }}
                >
                  <span role="img" aria-label="megaphone">
                    📣
                  </span>
                  お知らせ
                </div>

                <div
                  style={{
                    color: "#1b2a33",
                    fontSize: 14,
                    lineHeight: 1.4,
                    marginTop: 4,
                    marginBottom: 8,
                    textShadow: "0 1px 2px rgba(255,255,255,.45)",
                  }}
                >
                  {!latest ? (
                    <div style={{ color: "#334", opacity: 0.9 }}>
                      現在お知らせはありません。
                    </div>
                  ) : (
                    <>
                      {latestDate && <div style={{ marginBottom: 4 }}>{latestDate}</div>}
                      {latest.title && <div style={{ marginBottom: 4 }}>{latest.title}</div>}
                      {latestBodyLines.length > 0 ? (
                        latestBodyLines.map((line, idx) => <div key={idx}>{line}</div>)
                      ) : (
                        <div>お知らせの本文が登録されていません。</div>
                      )}
                    </>
                  )}
                </div>

                <div style={{ textAlign: "right" }}>
                  <span
                    style={{
                      fontSize: 12,
                      color: "#1b2a33",
                      fontWeight: 700,
                      textDecoration: "underline",
                      textUnderlineOffset: 2,
                    }}
                  >
                    一覧を見る →
                  </span>
                </div>
              </div>
            </div>
          </a>
        </section>

        {/* =========================
            ⑥ oshirase-bt.webp（パネルの下）
           ========================= */}
        <div className="center mt12">
          <a href="/notices" aria-label="お知らせを見る">
            <img
              className={`bigBtn floaty btnOshiraseFloat`}
              src={btnOshirase}
              alt="お知らせを見る"
            />
          </a>
        </div>

        {/* =========================
            ⑦ teleboat-bt.webp（ふわふわ無し）
           ========================= */}
        <div className="mt16">
          <img className="banner" src={bannerTeleboat} alt="TELEBOAT" />
        </div>

        {/* =========================
            ⑧ boatrace-bt.webp（ふわふわ無し）
           ========================= */}
        <div className="mt10">
          <img className="banner" src={bannerBoatrace} alt="BOAT RACE" />
        </div>
      </div>

      <Footer />
    </main>
  );
}